import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

type Variant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span" | "p";

type StatusType =
  | "ASSIGNED"
  | "DRAFT"
  | "FAILED"
  | "PENDING"
  | "secondary"
  | "subtle"
  | "error"
  | "success"
  | "info";

const getStatusStyles = (status: StatusType) => {
  switch (status) {
    case "ASSIGNED":
      return "!bg-green-100 !dark:bg-green-900 !text-green-800 dark:!text-green-100";
    case "DRAFT":
      return "!bg-gray-100 !dark:bg-gray-700 !text-gray-800 dark:!text-gray-100";
    case "FAILED":
      return "!bg-red-100 dark:!bg-red-900 !text-red-800 dark:!text-red-100";
    case "secondary":
      return "text-sm !text-gray-500 dark:!text-gray-300";
    case "subtle":
      return "text-sm !text-gray-600 dark:!text-gray-400";
    case "error":
      return "!text-red-600 dark:!text-red-400";
    case "success":
      return "!text-emerald-700 dark:!text-emerald-300";
    case "info":
      return "!text-blue-600 dark:!text-blue-200";
    default:
      return "!bg-yellow-100 dark:!bg-yellow-900 !text-yellow-800 dark:!text-yellow-100";
  }
};

const textVariants = cva("text-gray-900 dark:text-gray-100", {
  variants: {
    variant: {
      h1: "text-2xl font-bold",
      h2: "text-xl font-semibold",
      h3: "text-lg font-semibold",
      h4: "text-base font-semibold",
      h5: "",
      h6: "",
      span: "text-sm",
      p: "text-gray-700 dark:text-gray-300",
    },
    size: {
      xs: "!text-xs",
      sm: "!text-sm",
      base: "!text-base",
      lg: "!text-lg",
      xl: "!text-xl",
      "2xl": "!text-2xl",
      "3xl": "!text-3xl",
      "4xl": "!text-4xl",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
    },
  },
});

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children" | "translate">,
    VariantProps<typeof textVariants> {
  asChild?: boolean;
  children: React.ReactNode;
  values?: Record<string, string | number | undefined>;
  status?: StatusType;
}

type ElementType = {
  [K in Variant]: React.ElementRef<K>;
};

const getElementType = (variant: Variant | undefined) => {
  return (variant || "p") as keyof JSX.IntrinsicElements;
};

const TextDefault = React.forwardRef<ElementType[Variant], TextProps>(
  (
    {
      variant,
      className,
      size,
      weight,
      asChild = false,
      children,
      values,
      status,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const Component = asChild ? Slot : getElementType(variant as Variant);

    const processedValues = React.useMemo(() => {
      if (!values) return undefined;

      // Convert any undefined values to empty strings
      return Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === undefined ? "" : value,
        ]),
      );
    }, [values]);

    const content = React.useMemo(() => {
      if (typeof children !== "string") return children;

      try {
        return t(children, processedValues);
      } catch (error) {
        console.warn("Translation failed:", error);
        return children;
      }
    }, [children, t, processedValues]);

    const combinedClassName = React.useMemo(() => {
      const baseClasses = twMerge(
        textVariants({ variant, size, weight, className }),
        className,
      );
      return status ? `${baseClasses} ${getStatusStyles(status)}` : baseClasses;
    }, [variant, size, weight, className, status]);

    return React.createElement(Component, {
      className: combinedClassName,
      ref: ref as never,
      ...props,
      children: content,
    });
  },
);

TextDefault.displayName = "TextDefault";

export { TextDefault, textVariants };
